import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Pagination } from "./AdminShared";
import { StatusBadge, fmtDate, daysLeft } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Settings2 } from "lucide-react";

export default function AdminSubscriptions() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [manage, setManage] = useState(null);
  const [days, setDays] = useState(30);
  const [expiry, setExpiry] = useState("");

  const { data } = useQuery({
    queryKey: ["admin-subs", status, search, page],
    queryFn: async () => (await api.get("/admin/subscriptions", { params: { status, search, page, limit: 10 } })).data,
  });

  const act = useMutation({
    mutationFn: async ({ id, payload }) => (await api.put(`/admin/subscriptions/${id}`, payload)).data,
    onSuccess: () => {
      toast.success("Subscription updated");
      qc.invalidateQueries({ queryKey: ["admin-subs"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      setManage(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const items = data?.items || [];

  return (
    <div>
      <PageHeader title="Subscriptions" subtitle="Create, extend, pause and cancel subscriptions" />
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="sub-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search by customer or package…" className="pl-10 bg-white/5 border-white/10 text-white" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger data-testid="sub-status-filter" className="w-full sm:w-48 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0b0c12] border-white/10 text-white">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400">Customer</TableHead>
                <TableHead className="text-slate-400">Package</TableHead>
                <TableHead className="text-slate-400">Expiry</TableHead>
                <TableHead className="text-slate-400">Days Left</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400 text-right">Manage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">No subscriptions found.</TableCell></TableRow>
              ) : items.map((s) => (
                <TableRow key={s.id} className="border-white/10" data-testid={`admin-sub-row-${s.id}`}>
                  <TableCell><div className="text-white font-medium">{s.user_name}</div><div className="text-xs text-slate-500">{s.user_email}</div></TableCell>
                  <TableCell className="text-slate-300">{s.package_name}</TableCell>
                  <TableCell className="text-slate-400 text-sm">{fmtDate(s.expiry_date)}</TableCell>
                  <TableCell className="text-slate-300">{s.status === "active" ? daysLeft(s.expiry_date) : "—"}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`sub-manage-${s.id}`} size="sm" variant="ghost" onClick={() => { setManage(s); setExpiry(s.expiry_date?.slice(0, 10) || ""); }} className="text-slate-300 hover:text-white"><Settings2 className="w-4 h-4 mr-1" /> Manage</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination page={page} total={data?.total || 0} limit={10} onPage={setPage} />
      </div>

      <Dialog open={!!manage} onOpenChange={(v) => !v && setManage(null)}>
        <DialogContent className="bg-[#0b0c12] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">Manage Subscription</DialogTitle></DialogHeader>
          {manage && (
            <div className="space-y-4 text-sm">
              <div className="glass rounded-xl p-3">
                <div className="text-white font-medium">{manage.user_name} · {manage.package_name}</div>
                <div className="text-xs text-slate-500">Expires {fmtDate(manage.expiry_date)} · <StatusBadge status={manage.status} /></div>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-slate-300">Extend by days</Label>
                  <Input data-testid="sub-extend-days" type="number" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" />
                </div>
                <Button data-testid="sub-extend-btn" onClick={() => act.mutate({ id: manage.id, payload: { action: "extend", days: Number(days) } })} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">Extend</Button>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-slate-300">Set expiry date</Label>
                  <Input data-testid="sub-expiry-date" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" />
                </div>
                <Button data-testid="sub-setexpiry-btn" onClick={() => act.mutate({ id: manage.id, payload: { action: "extend", days: 0, expiry_date: new Date(expiry).toISOString() } })} className="bg-white/10 text-white hover:bg-white/20">Set</Button>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button data-testid="sub-pause-btn" onClick={() => act.mutate({ id: manage.id, payload: { action: "pause" } })} className="bg-blue-600 hover:bg-blue-500 text-white border-0">Pause</Button>
            <Button data-testid="sub-resume-btn" onClick={() => act.mutate({ id: manage.id, payload: { action: "resume" } })} className="bg-emerald-600 hover:bg-emerald-500 text-white border-0">Resume</Button>
            <Button data-testid="sub-cancel-btn" onClick={() => act.mutate({ id: manage.id, payload: { action: "cancel" } })} className="bg-red-600 hover:bg-red-500 text-white border-0">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
